import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/lib/auth';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

export interface OrganizationInvitation {
  id: string;
  organization_id: string;
  email: string;
  org_role: 'member' | 'super_user' | 'owner';
  invited_by_user_id: string;
  status: 'pending' | 'accepted' | 'cancelled';
  personal_message?: string;
  created_at: string;
  accepted_at?: string;
  accepted_by_user_id?: string;
}

export const useOrganizationInvitations = (organizationId?: string) => {
  const { user, refreshUser } = useAuth();
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Fetch invitations sent by this organization
  const fetchInvitations = async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('organization_invitations')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvitations((data || []) as OrganizationInvitation[]);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch pending invitations for current user's email
  const fetchPendingInvitations = async () => {
    if (!user?.email) {
      console.log('[useOrganizationInvitations] No user email, skipping fetch');
      return;
    }

    console.log('[useOrganizationInvitations] Fetching pending invitations for:', user.email);
    
    try {
      const { data, error } = await supabase
        .from('organization_invitations')
        .select(`
          id,
          organization_id,
          email,
          org_role,
          personal_message,
          status,
          created_at,
          invited_by_user_id,
          organizations!organization_invitations_organization_id_fkey (
            name
          )
        `)
        .ilike('email', user.email)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[useOrganizationInvitations] Error fetching:', error);
        throw error;
      }

      console.log('[useOrganizationInvitations] Fetched data:', data);

      const mappedInvitations = (data || []).map((inv: any) => ({
        ...inv,
        organization_name: inv.organizations?.name || 'Unknown Organization'
      }));
      
      console.log('[useOrganizationInvitations] Mapped invitations:', mappedInvitations);
      setPendingInvitations(mappedInvitations);
    } catch (error) {
      console.error('[useOrganizationInvitations] Error fetching pending invitations:', error);
    }
  };

  const sendInvitation = async (email: string, orgRole: 'member' | 'super_user' | 'owner', personalMessage?: string) => {
    if (!user?.organization_id) {
      throw new Error('Organization ID not found');
    }

    try {
      setLoading(true);

      // Check if invitation already exists
      const { data: existingInvitation } = await supabase
        .from('organization_invitations')
        .select('*')
        .eq('organization_id', user.organization_id)
        .eq('email', email)
        .eq('status', 'pending')
        .maybeSingle();

      if (existingInvitation) {
        throw new Error('An invitation to this email already exists');
      }

      // Create invitation (no token, no expiry)
      const { error: inviteError } = await supabase
        .from('organization_invitations')
        .insert([{
          organization_id: user.organization_id,
          email: email,
          invited_by_user_id: user.id,
          org_role: orgRole,
          personal_message: personalMessage,
          status: 'pending',
        }]);

      if (inviteError) throw inviteError;

      // Get user name from client profile
      const { data: clientProfile } = await supabase
        .from('client_profiles')
        .select('first_name, last_name')
        .eq('user_id', user.id)
        .single();

      const inviterName = clientProfile?.first_name && clientProfile?.last_name
        ? `${clientProfile.first_name} ${clientProfile.last_name}`
        : user.email || 'Someone';

      // Get organization name
      const { data: orgData } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', user.organization_id)
        .single();

      // Queue email
      await invokeEdgeFunction('queue-email', {
        body: {
          recipient_email: email,
          template_name: 'organization_invitation',
          template_data: {
            inviter_name: inviterName,
            organization_name: orgData?.name || 'an organization',
            role: orgRole,
            personal_message: personalMessage,
            app_url: window.location.origin,
            acceptUrl: `${window.location.origin}/auth/login`,
          },
          send_immediately: true,
        },
        callerOperation: 'useOrganizationInvitations.sendInvitation',
      });

      await fetchInvitations();
      
      // Track team invitation
      analytics.track({
        name: ANALYTICS_EVENTS.TEAM_MEMBER_INVITED,
        category: 'engagement',
        properties: {
          invited_role: orgRole,
        },
      });

      toast({
        title: "Invitation Sent",
        description: `An invitation has been sent to ${email}. They'll see it after signing up or logging in.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const acceptInvitation = async (invitationId: string) => {
    try {
      setLoading(true);

      // Call the database function to accept invitation
      const { data, error } = await supabase.rpc('accept_organization_invitation', {
        p_invitation_id: invitationId
      });

      if (error) throw error;
      
      // Check for error in response data
      if (data && typeof data === 'object' && 'error' in data) {
        throw new Error((data as any).error);
      }

      // Refresh user session
      if (refreshUser) {
        await refreshUser();
      }

      await fetchInvitations();
      await fetchPendingInvitations();

      return data;
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    try {
      // Get invitation details before cancelling
      const { data: invitation } = await supabase
        .from('organization_invitations')
        .select('email')
        .eq('id', invitationId)
        .single();

      const { error } = await supabase
        .from('organization_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId);

      if (error) throw error;

      // Send decline notification
      if (invitation?.email) {
        await invokeEdgeFunction('invitation-declined-notify', {
          body: {
            invitationId,
            declinedByEmail: invitation.email,
          },
          callerOperation: 'useOrganizationInvitations.cancelInvitation',
        });
      }

      await fetchInvitations();
      await fetchPendingInvitations();

      toast({
        title: 'Invitation Declined',
        description: 'The invitation has been declined.',
      });
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast({
        title: 'Error',
        description: 'Failed to decline invitation',
        variant: 'destructive',
      });
      throw error;
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchInvitations();
    }
    if (user?.email) {
      fetchPendingInvitations();
    }
  }, [organizationId, user?.email]);

  return { 
    invitations,
    pendingInvitations,
    loading, 
    sendInvitation, 
    acceptInvitation, 
    cancelInvitation,
    refetch: () => {
      fetchInvitations();
      fetchPendingInvitations();
    },
  };
};
